import { RightOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import styled from 'styled-components';
import React from 'react';
import { FacetFilterInput, FacetMetadata } from '../../../types.generated';
import { useAccessibility } from '../../../AccessibilityHandler';
import { capitalizeFirstLetterOnly } from '../../shared/textUtil';
import OptionsDropdownMenu from './OptionsDropdownMenu';
import useSearchFilterDropdown from './useSearchFilterDropdown';
import { IconWrapper } from './SearchFilterView';
import { getFilterDropdownIcon, manageMoreFilterAccessibility } from './utils';
import { MoreFilterOptionLabel } from './styledComponents';
import { useTranslation } from '../../../i18n/useTranslation';

const IconNameWrapper = styled.div`
    display: flex;
    overflow: hidden;
    align-items: center;
`;

interface Props {
    filter: FacetMetadata;
    activeFilters: FacetFilterInput[];
    onChangeFilters: (newFilters: FacetFilterInput[]) => void;
}

export default function MoreFilterOption({ filter, activeFilters, onChangeFilters }: Props) {
    const { t } = useTranslation();
    const {
        isMenuOpen,
        updateIsMenuOpen,
        updateFilters,
        filterOptions,
        numActiveFilters,
        areFiltersLoading,
        searchQuery,
        updateSearchQuery,
    } = useSearchFilterDropdown({
        filter,
        activeFilters,
        onChangeFilters,
    });
    const filterIcon = getFilterDropdownIcon(filter.field);
    const isAccessibilityEnabled: boolean = useAccessibility();

    function handleBlur(dropdownId) {
        // When a dropdown loses focus, close it after a delay
        if (isAccessibilityEnabled) {
            setTimeout(() => {
                const { activeElement } = document;
                const dropdownElement = document.getElementById(dropdownId);
                const checkToUpdateIsMenuOpen =
                    dropdownElement && !dropdownElement.contains(activeElement) && activeElement?.id !== dropdownId;

                if (checkToUpdateIsMenuOpen) {
                    updateIsMenuOpen(false);
                }
            }, 100); // Small delay to ensure focus hasn't moved back within the dropdown
        }
    }

    return (
        <Dropdown
            trigger={['click']}
            menu={{ items: filterOptions }}
            open={isMenuOpen}
            onOpenChange={(open) => updateIsMenuOpen(open)}
            dropdownRender={(menu) => (
                <OptionsDropdownMenu
                    menu={menu}
                    field={filter.field}
                    updateFilters={updateFilters}
                    searchQuery={searchQuery}
                    updateSearchQuery={updateSearchQuery}
                    isLoading={areFiltersLoading}
                    searchPlaceholder={filter.displayName || ''}
                    alignRight
                />
            )}
        >
            <MoreFilterOptionLabel
                tabIndex={0}
                key={filter.field}
                isOpen={isMenuOpen}
                isActive={!!numActiveFilters}
                id={`moreFilter${filter.field}`}
                onBlur={() => handleBlur(filter.field)}
                onClick={() => updateIsMenuOpen(!isMenuOpen)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        updateIsMenuOpen(!isMenuOpen);
                    } else {
                        manageMoreFilterAccessibility(e);
                    }
                }}
                className={numActiveFilters ? 'f-color-high-contrast' : ''}
                data-testid={`more-filter-${capitalizeFirstLetterOnly(filter.displayName)}`}
            >
                <IconNameWrapper>
                    {filterIcon && <IconWrapper>{filterIcon}</IconWrapper>}
                    <span className="truncate">{t(capitalizeFirstLetterOnly(filter.displayName) || '')} </span>
                    <span className="ml-1">{numActiveFilters ? `(${numActiveFilters}) ` : ''}</span>
                </IconNameWrapper>
                <RightOutlined style={{ fontSize: '12px', height: '12px' }} />
            </MoreFilterOptionLabel>
        </Dropdown>
    );
}
